import {
  CheckIcon,
  ChevronDownIcon,
  CopyIcon,
  MoreHorizontalIcon,
  PencilIcon,
  PlusIcon,
  Trash2Icon,
  VariableIcon,
} from "lucide-react";
import { observer } from "mobx-react-lite";
import { type KeyboardEvent as ReactKeyboardEvent, useEffect, useRef, useState } from "react";

import { usePlayground } from "@/components/Playground/playground-store";
import { TOUR_TARGET_IDS } from "@/components/Playground/tour/constants";
import type { VariableSet } from "@/components/Playground/types";
import { getVariableValueIsFilled } from "@/components/Playground/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown";
import { cn } from "@/lib/utils";

const useSetCount = (set?: VariableSet | null) => {
  const playground = usePlayground();
  const chatSettingsKeys = new Set(Object.values(playground.chat_settings ?? {}).filter(Boolean));
  const total = playground.detectedInputVariables.length;
  const filled = playground.detectedInputVariables.filter(key => {
    if (playground.isSessionModeChat && chatSettingsKeys.has(key)) {
      return true;
    }

    return getVariableValueIsFilled(set?.variables.find(variable => variable.key === key)?.value);
  }).length;

  return { filled, total };
};

const getVariableSetStatus = (filled: number, total: number) => {
  const missing = Math.max(total - filled, 0);

  if (total === 0) {
    return {
      isReady: true,
      text: "No variables",
    };
  }

  return {
    isReady: missing === 0,
    text: missing === 0 ? "Ready" : `${missing} missing`,
  };
};

const VariableSetCount = ({ filled, total }: { filled: number; total: number }) => {
  const { isReady } = getVariableSetStatus(filled, total);

  if (total === 0) {
    return null;
  }

  return (
    <span
      className={cn(
        "inline-flex h-5 shrink-0 items-center justify-end text-xs font-medium tabular-nums leading-none",
        isReady ? "text-gray-400" : "text-amber-600",
      )}
    >
      {filled}/{total}
    </span>
  );
};

interface VariableSetRowProps {
  set: VariableSet;
  onEdit: (set: VariableSet) => void;
  onRename: (set: VariableSet) => void;
  onDuplicate: (set: VariableSet) => void;
}

const VariableSetRow = observer(({ set, onEdit, onRename, onDuplicate }: VariableSetRowProps) => {
  const playground = usePlayground();
  const isActive = playground.activeInputVariableSetId === set.id;
  const { filled, total } = useSetCount(set);
  const setName = set.name || "Untitled set";

  const handleSelect = () => {
    playground.setActiveInputVariableSetId(set.id);
  };

  return (
    <div className="group flex items-center rounded-md focus-within:bg-gray-50 hover:bg-gray-50">
      <DropdownMenuItem
        onSelect={handleSelect}
        className={cn(
          "min-w-0 flex-1 cursor-pointer gap-2 rounded-md bg-transparent px-2 py-2 text-gray-700 focus:bg-gray-100",
          isActive && "bg-gray-100 text-gray-900",
        )}
      >
        <span className="flex h-5 w-4 shrink-0 items-center justify-center text-gray-400">
          {isActive ? (
            <CheckIcon className="h-3.5 w-3.5 text-gray-600" />
          ) : (
            <VariableIcon className="h-3.5 w-3.5" />
          )}
        </span>
        <span
          className={cn("min-w-0 flex-1 truncate text-sm leading-5", isActive && "font-medium")}
        >
          {setName}
        </span>
        <VariableSetCount filled={filled} total={total} />
      </DropdownMenuItem>
      <DropdownMenuSub>
        <DropdownMenuSubTrigger
          aria-label={`Actions for ${setName}`}
          className="mr-1 flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-md p-0 text-gray-400 hover:bg-gray-100 hover:text-gray-700 focus:bg-gray-100 focus:text-gray-700 data-[state=open]:bg-gray-100 data-[state=open]:text-gray-700 [&>svg:last-child]:hidden"
        >
          <MoreHorizontalIcon className="h-4 w-4" />
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent className="w-44 rounded-lg border-gray-200 p-1 shadow-lg">
          <DropdownMenuItem
            onSelect={() => onEdit(set)}
            className="h-9 cursor-pointer rounded-md text-gray-700"
          >
            <VariableIcon className="h-4 w-4 text-gray-400" />
            <span>Edit Values</span>
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => onRename(set)}
            className="h-9 cursor-pointer rounded-md text-gray-700"
          >
            <PencilIcon className="h-4 w-4 text-gray-400" />
            <span>Rename</span>
          </DropdownMenuItem>
          {playground.canAddInputVariableSet && (
            <DropdownMenuItem
              onSelect={() => onDuplicate(set)}
              className="h-9 cursor-pointer rounded-md text-gray-700"
            >
              <CopyIcon className="h-4 w-4 text-gray-400" />
              <span>Duplicate</span>
            </DropdownMenuItem>
          )}
          {playground.canRemoveInputVariableSet && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={() => playground.removeInputVariableSet(set.id)}
                className="h-9 cursor-pointer rounded-md text-red-600 focus:text-red-600"
              >
                <Trash2Icon className="h-4 w-4" />
                <span>Delete</span>
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    </div>
  );
});

const NewVariableSetButton = observer(() => {
  const playground = usePlayground();

  if (!playground.canAddInputVariableSet) {
    return null;
  }

  return (
    <Button
      id={TOUR_TARGET_IDS.INPUT_VARIABLE_SETS}
      type="button"
      variant="outline"
      size="sm"
      onClick={() => playground.addEmptyInputVariableSet()}
      className="h-8 gap-1.5 px-2.5 leading-none text-gray-700"
    >
      <PlusIcon className="h-3.5 w-3.5" />
      New Set
    </Button>
  );
});

export const VariableSetSelector = observer(() => {
  const playground = usePlayground();
  const activeSet = playground.activeInputVariableSet;
  const { filled, total } = useSetCount(activeSet);
  const { text: statusText } = getVariableSetStatus(filled, total);
  const [renamingSetId, setRenamingSetId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState("");
  const nameInputRef = useRef<HTMLInputElement>(null);
  const isChatScope = playground.isSessionModeChat;
  const renamingSet = renamingSetId
    ? playground.input_variable_sets.find(set => set.id === renamingSetId)
    : null;

  useEffect(() => {
    if (renamingSet) {
      nameInputRef.current?.focus();
      nameInputRef.current?.select();
    }
  }, [renamingSet]);

  if (!activeSet) {
    return <NewVariableSetButton />;
  }

  const handleNewSet = () => {
    if (!playground.canAddInputVariableSet) {
      return;
    }
    playground.addEmptyInputVariableSet();
  };

  const handleDuplicateSet = (set: VariableSet) => {
    if (!playground.canAddInputVariableSet) {
      return;
    }
    playground.duplicateInputVariableSet(set);
  };

  const handleOpenFullEditor = (set: VariableSet) => {
    playground.setActiveInputVariableSetId(set.id);
    playground.startEditingVariableSet(set.id);
  };

  const handleStartRename = (set: VariableSet) => {
    setDraftName(set.name || "");
    setRenamingSetId(set.id);
  };

  const commitRename = () => {
    if (renamingSetId) {
      playground.updateInputVariableSetName(renamingSetId, draftName.trim() || "Untitled set");
    }
    setRenamingSetId(null);
  };

  const cancelRename = () => {
    setDraftName("");
    setRenamingSetId(null);
  };

  const handleRenameKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      commitRename();
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      cancelRename();
    }
  };

  if (renamingSet) {
    return (
      <div
        id={TOUR_TARGET_IDS.INPUT_VARIABLE_SETS}
        className="flex h-8 min-w-0 max-w-full items-center"
      >
        <div className="inline-flex h-8 w-52 max-w-full items-center gap-1.5 rounded-full border border-gray-200/70 bg-gray-50/80 px-2.5 text-sm text-gray-700">
          <VariableIcon className="h-4 w-4 shrink-0 text-gray-400" />
          <Input
            ref={nameInputRef}
            value={draftName}
            aria-label="Input set name"
            onChange={event => setDraftName(event.target.value)}
            onBlur={commitRename}
            onKeyDown={handleRenameKeyDown}
            className="h-8 min-w-0 flex-1 border-0 bg-transparent px-0 text-sm leading-5 shadow-none outline-none ring-0 focus-visible:outline-none focus-visible:ring-0"
          />
        </div>
      </div>
    );
  }

  return (
    <div
      id={TOUR_TARGET_IDS.INPUT_VARIABLE_SETS}
      className="flex h-8 min-w-0 max-w-full items-center"
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="inline-flex h-8 max-w-full items-center justify-start gap-1.5 rounded-full border border-gray-200/70 bg-gray-50/80 px-2.5 text-sm font-medium leading-none text-gray-600 shadow-none transition-colors hover:border-gray-300 hover:bg-gray-100/80 hover:text-gray-900 focus:outline-none focus-visible:ring-1 focus-visible:ring-gray-300 data-[state=open]:border-gray-300 data-[state=open]:bg-gray-100 data-[state=open]:text-gray-900"
            aria-label={`Input variable scope: ${activeSet.name || "Untitled set"}. ${filled} of ${total} variables filled. ${statusText}. ${
              isChatScope ? "Used for this chat." : "Shown in the prompt editor."
            }`}
            title={
              isChatScope
                ? `Chat input set: ${activeSet.name || "Untitled set"}`
                : `Editor input scope: ${activeSet.name || "Untitled set"}`
            }
          >
            <VariableIcon className="h-4 w-4 shrink-0 text-gray-400" />
            <span className="min-w-0 max-w-[180px] truncate leading-5 text-gray-700">
              {activeSet.name || "Untitled set"}
            </span>
            <VariableSetCount filled={filled} total={total} />
            <ChevronDownIcon className="h-4 w-4 shrink-0 text-gray-400" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="w-64 rounded-lg border-gray-200 p-1 shadow-lg"
        >
          <DropdownMenuLabel className="px-2 py-1.5">
            <span className="block text-xs font-medium text-gray-500">Input variable scope</span>
            <span className="mt-0.5 block text-[11px] font-normal leading-4 text-gray-400">
              {isChatScope
                ? "Choose the input set used for this chat and shown in the editor."
                : "Choose the set shown in the prompt editor. All input sets still run together."}
            </span>
          </DropdownMenuLabel>
          {playground.input_variable_sets.map(set => (
            <VariableSetRow
              key={set.id}
              set={set}
              onEdit={handleOpenFullEditor}
              onRename={handleStartRename}
              onDuplicate={handleDuplicateSet}
            />
          ))}
          {playground.canAddInputVariableSet && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={handleNewSet}
                className="h-9 cursor-pointer rounded-md text-gray-700"
              >
                <PlusIcon className="h-4 w-4 text-gray-400" />
                <span className="text-sm leading-5">New Set</span>
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
});

export default VariableSetSelector;

export const VariableSetsEmptyStateBanner = observer(() => {
  const playground = usePlayground();

  if (playground.detectedInputVariables.length === 0 || playground.activeInputVariableSet) {
    return null;
  }

  return (
    <div className="mx-4 mt-3 flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600">
      <span>
        Values are stored in an input set. Create one to fill variables, keep several versions, and
        switch between them from the outputs drawer.
      </span>
      {playground.canAddInputVariableSet && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => playground.addEmptyInputVariableSet()}
          className="shrink-0"
        >
          New Set
        </Button>
      )}
    </div>
  );
});
